import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Box,
    IconButton,
    Typography,
    Chip,
    InputAdornment,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_PRODUCT, EDIT_PRODUCT } from 'src/graphql/mutations/products';
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/iconify';

export default function ProductDialog({ open, onClose, product, refetch }) {
    const { enqueueSnackbar } = useSnackbar();
    const [formData, setFormData] = useState({
        productName: '',
        amount: '',
        description: '',
        isAddOn: false,
        isActive: true,
        questions: [],
        scope: [],
    });
    const [newQuestion, setNewQuestion] = useState('');
    const [newScope, setNewScope] = useState('');

    const [createProduct, { loading: createLoading }] = useMutation(CREATE_PRODUCT, {
        onCompleted: () => {
            enqueueSnackbar('Product created successfully!', { variant: 'success' });
            refetch();
            handleClose();
        },
        onError: (error) => {
            enqueueSnackbar(error.message || 'Failed to create product', { variant: 'error' });
        },
    });

    const [editProduct, { loading: editLoading }] = useMutation(EDIT_PRODUCT, {
        onCompleted: () => {
            enqueueSnackbar('Product updated successfully!', { variant: 'success' });
            refetch();
            handleClose();
        },
        onError: (error) => {
            enqueueSnackbar(error.message || 'Failed to update product', { variant: 'error' });
        },
    });

    useEffect(() => {
        if (product) {
            setFormData({
                productName: product.productName || '',
                amount: product.amount || '',
                description: product.description || '',
                isAddOn: product.isAddOn || false,
                isActive: product.isActive || true,
                questions: product.questions || [],
                scope: product.scope || [],
            });
        } else {
            setFormData({
                productName: '',
                amount: '',
                description: '',
                isAddOn: false,
                isActive: true,
                questions: [],
                scope: [],
            });
        }
        setNewQuestion('');
        setNewScope('');
    }, [product, open]);

    const handleClose = () => {
        setFormData({
            productName: '',
            amount: '',
            description: '',
            isAddOn: false,
            isActive: true,
            questions: [],
            scope: [],
        });
        setNewQuestion('');
        setNewScope('');
        onClose();
    };

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: e.target.type === 'checkbox' ? checked : value,
        }));
    };

    const handleAddQuestion = () => {
        if (newQuestion.trim()) {
            setFormData((prev) => ({
                ...prev,
                questions: [...prev.questions, newQuestion.trim()],
            }));
            setNewQuestion('');
        }
    };

    const handleRemoveQuestion = (index) => {
        setFormData((prev) => ({
            ...prev,
            questions: prev.questions.filter((_, i) => i !== index),
        }));
    };

    const handleAddScope = () => {
        if (newScope.trim()) {
            setFormData((prev) => ({
                ...prev,
                scope: [...prev.scope, newScope.trim()],
            }));
            setNewScope('');
        }
    };

    const handleRemoveScope = (index) => {
        setFormData((prev) => ({
            ...prev,
            scope: prev.scope.filter((_, i) => i !== index),
        }));
    };

    const handleSubmit = () => {
        if (!formData.productName || !formData.amount) {
            enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
            return;
        }

        const input = {
            productName: formData.productName,
            amount: parseFloat(formData.amount),
            description: formData.description,
            isAddOn: formData.isAddOn,
            isActive: formData.isActive,
            questions: formData.questions,
            scope: formData.scope,
        };

        if (product) {
            editProduct({
                variables: {
                    input: {
                        productId: product._id,
                        ...input,
                    },
                },
            });
        } else {
            createProduct({ variables: { input } });
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{product ? 'Edit Product' : 'Create New Product'}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        fullWidth
                        required
                        label="Product Name"
                        name="productName"
                        value={formData.productName}
                        onChange={handleChange}
                    />

                    <TextField
                        fullWidth
                        required
                        label="Amount"
                        name="amount"
                        type="number"
                        value={formData.amount}
                        onChange={handleChange}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                        }}
                    />

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    name="isAddOn"
                                    checked={formData.isAddOn}
                                    onChange={handleChange}
                                />
                            }
                            label="Is Add-on"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleChange}
                                />
                            }
                            label="Is Active"
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Scope
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Add scope item"
                                value={newScope}
                                onChange={(e) => setNewScope(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddScope();
                                    }
                                }}
                            />
                            <Button onClick={handleAddScope} variant="outlined">
                                Add
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {formData.scope.map((item, index) => (
                                <Chip
                                    key={index}
                                    label={item}
                                    onDelete={() => handleRemoveScope(index)}
                                    size="small"
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Questions
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Add question"
                                value={newQuestion}
                                onChange={(e) => setNewQuestion(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddQuestion();
                                    }
                                }}
                            />
                            <Button onClick={handleAddQuestion} variant="outlined">
                                Add
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {formData.questions.map((question, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: 'background.neutral',
                                        p: 1,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        {index + 1}. {question}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveQuestion(index)}
                                        color="error"
                                    >
                                        <Iconify icon="eva:trash-2-fill" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={createLoading || editLoading}
                >
                    {product ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
